import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import DateRow from 'components/DateRow/DateRow'
import { SET_RECORD_TO_AMEND } from '../../WVManager/Workview2Reducer'
import Cross from '@tecuity/sos-icons/react/cross-heavy'
import format from 'date-fns/format'
import * as api from '../../../requests'
import { setErrorToast, setSuccessToast } from '../../../actions'

const LegacyFilingForm = forwardRef(
  ({ selectedSourceType, setLoading, closePaperModalUnlockItem }, ref) => {
    const dispatch = useDispatch()
    const {
      selectedRow,
      legacyFilingDetails: { Config = dummyConfig },
    } = useSelector((state) => state.workview2, shallowEqual)
    const [fieldData, setFieldData] = useState({})
    const [optionsMap, setOptionsMap] = useState({})

    useEffect(() => {
      if (Config.LegacyFilingFields) {
        const initFieldData = {}
        Config.LegacyFilingFields.forEach((field) => {
          initFieldData[field.FieldName] = getDefaultDataType(field.LegacyFilingFieldType)
          if (field.LegacyFilingFieldType === 3 && field.Endpoint) {
            // get options for dropdowns
            api.GET(
              field.Endpoint,
              (data) => {
                setOptionsMap((om) => ({
                  ...om,
                  [field.FieldName]: data.map((t) => ({
                    value: t.Id,
                    label: t.Name,
                  })),
                }))
              },
              (err) => console.error(err)
            )
          } else if (field.LegacyFilingFieldType === 2 && field.Options) {
            setOptionsMap((om) => ({
              ...om,
              [field.FieldName]: field.Options,
            }))
          }
        })
        setFieldData(initFieldData)
      }
    }, [Config])

    const getDefaultDataType = (type) => {
      switch (type) {
        case 1: // Text
        case 2: // StaticDropdown
        case 3: // ServerDropdown
        case 4: // Date
        case 5: // Number
          return ''
        default:
          return null
      }
    }

    const requiredFieldsAreFilled = useMemo(() => {
      // check all required fields are filled
      return Config.LegacyFilingFields.filter((f) => f.IsRequired).every(
        (f) => !!fieldData[f.FieldName]
      )
    }, [fieldData, Config])

    useImperativeHandle(ref, () => ({
      submitFiling() {
        if (!requiredFieldsAreFilled) {
          dispatch(setErrorToast('Missing Required Fields.'))
          return
        }

        setLoading(true)
        api.SUBMIT_LEGACY_FILING(
          {
            SourceTypeId: +selectedSourceType,
            RecordId: selectedRow.RecordToAmend.SourceID,
            FieldData: fieldData,
            WorkViewItemId: selectedRow.WorkViewItemId,
            ImageNum: selectedRow.DLN,
          },
          (res) => {
            dispatch(setSuccessToast(`Successfully Approved Legacy Filing ${res}`))
            setLoading(false)
            closePaperModalUnlockItem(true)
          },
          (err) => {
            console.error(err)
            dispatch(setErrorToast(err))
            setLoading(false)
          }
        )
      },
    }))

    return (
      <div>
        <div
          style={{
            textAlign: 'center',
            minWidth: '400px',
            padding: '15px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <ContextBar elevation="high">
            <ContextButton
              leftIcon={
                <div className="button-icon">
                  {' '}
                  <Cross />{' '}
                </div>
              }
              variant="danger"
              onClick={() => {
                dispatch({ type: SET_RECORD_TO_AMEND, record: null })
              }}
              customCss={''}
            >
              Cancel
            </ContextButton>
            <span className="primary">
              <h1 style={{ fontWeight: 'bold' }}>Legacy filing for:</h1>{' '}
              {selectedRow.RecordToAmend.Name ||
                selectedRow.RecordToAmend['Trademark Name'] ||
                selectedRow.RecordToAmend['Filing Number']}
            </span>
          </ContextBar>
        </div>
        <div className="legacy-filing-form">
          {(Config.LegacyFilingFields || [dummyLegacyFilingField]).map((field) => {
            switch (field.LegacyFilingFieldType) {
              case 1: // text
                return (
                  <div key={field.LegacyFilingFieldId}>
                    <div className="property-label">
                      {field.IsRequired ? (
                        <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                      ) : (
                        ''
                      )}
                      {`${field.FieldLabel}\n`}
                    </div>
                    <TextInput
                      aria-label={field.FieldLabel}
                      value={fieldData[field.FieldName] || ''}
                      onChange={(e) => {
                        setFieldData((data) => ({ ...data, [field.FieldName]: e.target.value }))
                      }}
                    />
                  </div>
                )
              case 2: // StaticDropdown
              case 3: // ServerDropdown
                return (
                  <div key={field.LegacyFilingFieldId}>
                    <div className="property-label">
                      {field.IsRequired ? (
                        <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                      ) : (
                        ''
                      )}
                      {`${field.FieldLabel}\n`}
                    </div>
                    <SelectBox
                      options={optionsMap[field.FieldName] || []}
                      description={field.LegacyFilingFieldType === 2 && (optionsMap[field.FieldName] || []).length === 0 
                        ? 'No options configured, please contact an admin.'
                        : null}
                      value={fieldData[field.FieldName] || ''}
                      aria-label={field.FieldLabel}
                      onChange={(e) => {
                        setFieldData((data) => ({ ...data, [field.FieldName]: e }))
                      }}
                    />
                  </div>
                )
              case 4: // Date
                return (
                  <div key={field.LegacyFilingFieldId}>
                    <div className="property-label">
                      {field.IsRequired ? (
                        <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                      ) : (
                        ''
                      )}
                      {`${field.FieldLabel}\n`}
                    </div>
                    <DateRow
                      rowLabel={field.FieldLabel}
                      date={
                        fieldData[field.FieldName] && fieldData[field.FieldName].length >= 10
                          ? format(new Date(fieldData[field.FieldName]), 'MM/dd/yyyy')
                          : fieldData[field.FieldName] || ''
                      }
                      setDate={(e) => {
                        setFieldData((data) => ({ ...data, [field.FieldName]: e }))
                        // setIsDirty(true)
                      }}
                    />
                  </div>
                )
              case 5: // Number
                return (
                  <div key={field.LegacyFilingFieldId}>
                    <div className="property-label">
                      {field.IsRequired ? (
                        <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                      ) : (
                        ''
                      )}
                      {`${field.FieldLabel}\n`}
                    </div>
                    <TextInput
                      customCss={`width: 20%; min-width: 150px;`}
                      aria-label={field.FieldLabel}
                      value={fieldData[field.FieldName] || ''}
                      onChange={(e) => {
                        setFieldData((data) => ({ ...data, [field.FieldName]: e.target.value }))
                      }}
                      type="number"
                      min="0"
                      step="1"
                      pattern="\d+"
                    />
                  </div>
                )
              default:
                console.warn(`No implementation for type: ${field.LegacyFilingFieldType}`)
                return null
            }
          })}
        </div>
        {/* <ContextButton onClick={submitFiling} customCss={{ display: 'flex', justifySelf: 'center' }}>
        Submit
      </ContextButton> */}
      </div>
    )
  }
)

export default LegacyFilingForm

const dummyConfig = {
  LegacyFilingConfigId: 169,
  SourceTypeId: 169,
  SourceType: null,
  LegacyFilingFields: [dummyLegacyFilingField],
  IsEnabled: true,
}

const dummyLegacyFilingField = {
  LegacyFilingFieldId: 16901,
  LegacyFilingConfigId: 169,
  LegacyFilingFieldType: 1,
  Endpoint: '',
  FieldName: '',
  FieldLabel: '',
  Order: 0,
  IsRequired: false,
  // Options: [],
}

// public enum LegacyFilingFieldTypeCode
// {
//     Text = 1,
//     StaticDropdown = 2,
//     ServerDropdown = 3,
//     Date = 4,
//     Number = 5
// }
